import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, setDoc, updateDoc, serverTimestamp, query, where, orderBy, arrayUnion, arrayRemove, onSnapshot } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import { useCart } from '../contexts/CartContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { useAuth } from '../contexts/AuthContext';
import { MapPin, Phone, Clock, Star, Search, Heart, ShoppingCart, CheckCircle, X, Send, ChevronLeft, ChevronRight, UtensilsCrossed, CircleX, ThumbsUp, Navigation } from 'lucide-react';
import MenuItemDetailModal from '../components/MenuItemDetailModal';
import CartSheet from '../components/ios/CartSheet';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import { loadCache, saveCache } from '../utils/dataCache';

// Mirrors the iOS customer RestaurantDetailView while preserving all
// existing web data flow: restaurant doc, menuItems + reviews
// subcollections, likes, per-item quick-add, toasts, category
// scroll-spy, owner photo, and cart sheet.
const RestaurantDetailPage = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const businessType = searchParams.get('businessType') === 'retail' ? 'retail' : 'food';
  const backToMenu = () => navigate(`/dashboard/menu?businessType=${businessType}`);

  const { db } = useFirebase();
  const { items: cartItems, getItemCount } = useCart();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { currentUser } = useAuth();

  const [restaurant, setRestaurant] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [userReview, setUserReview] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showOwnerPhotoModal, setShowOwnerPhotoModal] = useState(false);
  const [ownerProfileImageURL, setOwnerProfileImageURL] = useState(null);
  const [ownerName, setOwnerName] = useState(null);
  const [activeCategoryOnScroll, setActiveCategoryOnScroll] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMenuSectionsOpen, setIsMenuSectionsOpen] = useState(false);
  const [showCartSheet, setShowCartSheet] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isAppOffline, setIsAppOffline] = useState(false);
  const categoryRefs = useRef({});
  const sectionObserver = useRef(null);
  const reviewsUnsub = useRef(null);

  useEffect(() => {
    if (restaurantId) {
      // Hydrate from cache first (iOS CacheManager.loadBusinessProfile) —
      // instant render, then fetchRestaurantDetails refreshes + re-caches.
      const cachedRestaurant = loadCache(`restaurant_${restaurantId}`);
      const cachedItems = loadCache(`menuItems_${restaurantId}`);
      if (cachedRestaurant) {
        setRestaurant(cachedRestaurant);
        const likedBy = cachedRestaurant.likedBy || [];
        setLikeCount(cachedRestaurant.likeCount ?? likedBy.length);
        setHasLiked(currentUser ? likedBy.includes(currentUser.uid) : false);
      }
      if (cachedItems?.length) {
        setMenuItems(cachedItems);
        setLoading(false);
      }
      fetchRestaurantDetails();
      monitorOfflineStatus();
    }
    return () => {
      reviewsUnsub.current?.();
      reviewsUnsub.current = null;
    };
  }, [restaurantId]);

  const monitorOfflineStatus = () => {
    if (!db) return;
    const settingsRef = doc(db, 'settings', 'global');
    return onSnapshot(settingsRef, (snapshot) => {
      setIsAppOffline(snapshot.exists() ? (snapshot.data().isAppOffline || false) : false);
    });
  };

  const fetchRestaurantDetails = async () => {
    try {
      // No setLoading(true) — loading starts true; cache hydration may have
      // already cleared it, and we don't want to re-flash the spinner.

      // Fetch restaurant data
      const restaurantRef = doc(db, 'restaurants', restaurantId);
      const restaurantSnap = await getDoc(restaurantRef);

      if (restaurantSnap.exists()) {
        const data = restaurantSnap.data();
        const restaurantData = { id: restaurantSnap.id, ...data };
        setRestaurant(restaurantData);
        saveCache(`restaurant_${restaurantId}`, restaurantData);

        // Like state — likedBy array of user ids
        const likedBy = data.likedBy || [];
        setLikeCount(data.likeCount ?? likedBy.length);
        setHasLiked(currentUser ? likedBy.includes(currentUser.uid) : false);

        // Owner profile photo (for avatar modal)
        if (data.ownerId) {
          try {
            const ownerSnap = await getDoc(doc(db, 'users', data.ownerId));
            if (ownerSnap.exists()) {
              const owner = ownerSnap.data();
              setOwnerProfileImageURL(owner.profileImageURL || owner.photoURL || null);
              setOwnerName(owner.name || owner.displayName || data.ownerName || null);
            }
          } catch (e) {
            // owner profile is optional
          }
        } else {
          setOwnerProfileImageURL(data.ownerProfileImageURL || null);
          setOwnerName(data.ownerName || null);
        }

        // Fetch menu items from subcollection
        const menuItemsRef = collection(db, 'restaurants', restaurantId, 'menuItems');
        const menuSnapshot = await getDocs(query(menuItemsRef, orderBy('name')));
        const items = menuSnapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
          restaurantId,
          restaurantName: data.name,
          restaurantType: data.restaurantType || 'Fast Food',
          island: data.island || 'saintKitts'
        }));
        setMenuItems(items);
        saveCache(`menuItems_${restaurantId}`, items);

        // Fetch reviews — live listener, mirrors iOS RestaurantReviewViewModel
        fetchReviews();
      } else {
        setError('Restaurant not found');
      }
    } catch (err) {
      console.error('Error fetching restaurant details:', err);
      setError('Error loading restaurant');
    } finally {
      setLoading(false);
    }
  };

  // iOS RestaurantReviewViewModel.loadReviews — top-level `reviews`
  // collection, Approved only (rules gate non-admin reads the same way).
  const fetchReviews = () => {
    reviewsUnsub.current?.();
    const q = query(
      collection(db, 'reviews'),
      where('restaurantId', '==', restaurantId),
      where('status', '==', 'Approved'),
      orderBy('createdAt', 'desc')
    );
    reviewsUnsub.current = onSnapshot(q, (snapshot) => {
      const reviewsData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setReviews(reviewsData);
      if (currentUser) {
        setUserReview(reviewsData.find(r => r.userId === currentUser.uid) || null);
      }
    }, (err) => console.error('Error fetching reviews:', err));
  };

  // Scroll-spy: track which category section is in view
  useEffect(() => {
    if (sectionObserver.current) sectionObserver.current.disconnect();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveCategoryOnScroll(entry.target.dataset.category);
          }
        });
      },
      { rootMargin: '-120px 0px -60% 0px' }
    );
    sectionObserver.current = observer;
    Object.values(categoryRefs.current).forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [menuItems, selectedCategory, searchText]);

  const scrollToCategory = (category) => {
    const el = categoryRefs.current[category];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Filter items
  const filteredItems = menuItems.filter(item => {
    const matchesSearch = !searchText ||
      (item.name || '').toLowerCase().includes(searchText.toLowerCase()) ||
      (item.itemDescription || '').toLowerCase().includes(searchText.toLowerCase());
    return matchesSearch;
  });

  // Group by category for sectioned display
  const groupedMenuItems = filteredItems.reduce((acc, item) => {
    const cat = item.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  // Category order: menuCategories first, then any extras present in items
  const categories = (() => {
    const ordered = [...(restaurant?.menuCategories || [])];
    Object.keys(groupedMenuItems).forEach(cat => {
      if (!ordered.includes(cat)) ordered.push(cat);
    });
    return ordered.filter(cat => groupedMenuItems[cat]?.length > 0);
  })();

  const visibleCategories = selectedCategory ? [selectedCategory] : categories;

  // iOS "Menu Sections" panel data — categories + counts from ALL items
  // (restaurantMenuItems), not the search-filtered subset.
  const menuSections = (() => {
    const counts = menuItems.reduce((acc, item) => {
      const cat = item.category || 'Other';
      acc[cat] = (acc[cat] || 0) + 1;
      return acc;
    }, {});
    const ordered = [...(restaurant?.menuCategories || [])];
    Object.keys(counts).sort().forEach(cat => {
      if (!ordered.includes(cat)) ordered.push(cat);
    });
    return ordered.filter(cat => counts[cat]).map(cat => ({ name: cat, count: counts[cat] }));
  })();

  const selectSection = (category) => {
    // iOS sidebar selects (no toggle, panel stays open) — "All" clears the filter
    setSelectedCategory(category);
    if (category) scrollToCategory(category);
  };

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2500);
  };

  const handleToggleFavorite = (item, e) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate('/login');
      return;
    }
    toggleFavorite(item);
  };

  // Restaurant like — likes are stored on the restaurant doc (likedBy array)
  const toggleRestaurantLike = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!db || !restaurant) return;
    const restaurantRef = doc(db, 'restaurants', restaurantId);
    try {
      if (hasLiked) {
        await updateDoc(restaurantRef, { likedBy: arrayRemove(currentUser.uid) });
        setHasLiked(false);
        setLikeCount(c => Math.max(0, c - 1));
      } else {
        await updateDoc(restaurantRef, { likedBy: arrayUnion(currentUser.uid) });
        setHasLiked(true);
        setLikeCount(c => c + 1);
      }
    } catch (err) {
      console.error('Error toggling restaurant like:', err);
    }
  };

  // Submit review — mirrors iOS ReviewView
  const submitReview = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!reviewText.trim()) return;
    setReviewSubmitting(true);
    try {
      // iOS formatUserName — "J. Browne" (first initial + last name)
      const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
      const u = userSnap.exists() ? userSnap.data() : {};
      const first = (u.firstName || '').trim();
      const last = (u.lastName || '').trim();
      const userName = first && last
        ? `${first[0].toUpperCase()}. ${last}`
        : first || last || 'Anonymous';

      // iOS writes reviews/{uuid} with status 'Pending' — admins approve.
      // Rules require exactly these keys on create.
      const reviewId = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`).toUpperCase();
      await setDoc(doc(db, 'reviews', reviewId), {
        id: reviewId,
        restaurantId,
        userId: currentUser.uid,
        userName,
        rating: reviewRating,
        comment: reviewText.trim(),
        status: 'Pending',
        isLiked: false,
        createdAt: serverTimestamp()
      });
      setReviewSuccess(true);
      setTimeout(() => {
        setShowReviewModal(false);
        setReviewSuccess(false);
        setReviewText('');
        setReviewRating(5);
      }, 1500);
    } catch (err) {
      console.error('Error submitting review:', err);
    } finally {
      setReviewSubmitting(false);
    }
  };

  const getDirections = () => {
    const coords = restaurant?.coordinates;
    const destination = coords?.latitude
      ? `${coords.latitude},${coords.longitude}`
      : restaurant?.address
        ? encodeURIComponent(`${restaurant.address}, ${restaurant.parish || ''}, ${restaurant.island || ''}`)
        : null;
    if (!destination) return;
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}`, '_blank', 'noopener,noreferrer');
  };

  const formatReviewDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const isOpen = restaurant ? (restaurant.isAcceptingOrders !== false) : false;
  const itemCount = getItemCount();

  if (loading) {
    return (
      <div className="bg-[#F2F2F7] min-h-full flex flex-col items-center justify-center gap-3 py-20">
        <div className="w-8 h-8 rounded-full border-[3px] border-gray-200 border-t-[#FF3B30] animate-spin" />
        <p className="text-[15px] text-[#8E8E93]">Loading restaurant...</p>
      </div>
    );
  }

  if (error || !restaurant) {
    return (
      <div className="bg-[#F2F2F7] min-h-full flex flex-col items-center justify-center gap-4 py-20 px-8">
        <UtensilsCrossed size={48} className="text-gray-300" strokeWidth={1.5} />
        <h2 className="text-[20px] font-semibold text-gray-900">Restaurant not found</h2>
        <p className="text-[15px] text-[#8E8E93] text-center">{error || 'This restaurant is no longer available.'}</p>
        <button onClick={backToMenu} className="mt-2 text-[15px] font-semibold text-white bg-red-600 px-5 py-2.5 rounded-xl">
          Back to Menu
        </button>
      </div>
    );
  }

  return (
    <>
      <SEO title={restaurant.name} description={restaurant.description || `Order from ${restaurant.name}`} />
      <div className="bg-[#F2F2F7] min-h-full pb-32">
        {/* Banner — edge-to-edge like iOS: floating back + like buttons
            overlay the hero image instead of a title bar above it. */}
        <div className="relative min-h-[220px] bg-gray-300 flex">
          <button
            onClick={backToMenu}
            aria-label="Back"
            className="absolute top-3 left-3 z-20 w-9 h-9 rounded-full bg-black/45 flex items-center justify-center"
          >
            <ChevronLeft size={22} className="text-white" />
          </button>
          <button
            onClick={toggleRestaurantLike}
            aria-label="Like restaurant"
            className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-black/45 flex items-center justify-center"
          >
            <Heart size={18} className={hasLiked ? 'text-[#FF3B30]' : 'text-white'} fill={hasLiked ? '#FF3B30' : 'none'} />
          </button>

          {/* Top-right: online status dot only */}
          <div className="absolute top-[52px] right-3 z-20 flex items-center px-2.5 py-1.5 rounded-full bg-black/45">
            <span className={`w-2.5 h-2.5 rounded-full ${isOpen ? 'bg-green-400' : 'bg-gray-400'}`} />
          </div>

          {/* Bottom-right of banner: ⭐ rating badge — real reviews only */}
          {reviews.length > 0 && (
            <div className="absolute bottom-3 right-3 z-20 flex items-center gap-1 px-2 py-1 rounded-lg bg-black/60">
              <Star size={11} className="text-yellow-400 fill-yellow-400" />
              <span className="text-[11px] font-semibold text-white">{Number(restaurant.rating || 0).toFixed(1)}</span>
              <span className="text-[9px] text-white/80">({reviews.length})</span>
            </div>
          )}

          {restaurant.bannerURL ? (
            <img src={restaurant.bannerURL} alt={restaurant.name} className="absolute inset-0 w-full h-full object-contain" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center">
              <UtensilsCrossed size={40} className="text-white/60" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />

          {/* Overlay content anchored to bottom — iOS HStack(.top, spacing 16) */}
          <div className="relative flex items-end gap-4 w-full p-4 pt-24">
            {/* Owner profile photo + name (iOS left column) */}
            {(ownerProfileImageURL || ownerName) && (
              <button onClick={() => setShowOwnerPhotoModal(true)} className="shrink-0 flex flex-col items-center gap-1.5 pb-1">
                {ownerProfileImageURL ? (
                  <img src={ownerProfileImageURL} alt={ownerName || 'Owner'} className="w-16 h-16 rounded-full object-cover ring-2 ring-white/50" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-xl font-semibold text-white ring-2 ring-white/50">
                    {(ownerName || '?')[0]}
                  </div>
                )}
                {ownerName && (
                  <span className="text-[11px] font-semibold text-white max-w-[80px] truncate">{ownerName}</span>
                )}
              </button>
            )}

            {/* Business details (iOS right column) */}
            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex items-center gap-1.5">
                <h2 className="text-[22px] font-bold text-white drop-shadow-md truncate">{restaurant.name}</h2>
                {restaurant.isVerified && <CheckCircle size={15} className="text-[#30B0C7] drop-shadow-md shrink-0" />}
              </div>


              {restaurant.description && (
                <p className="text-[13px] text-white/95 line-clamp-3">{restaurant.description}</p>
              )}

              {/* Address / phone / hours — iOS secondary rows on the overlay */}
              <div className="space-y-1 pt-1">
                {restaurant.address && (
                  <button onClick={getDirections} className="flex items-center gap-1.5 text-left">
                    <MapPin size={14} className="text-white/80 shrink-0" />
                    <span className="text-[13px] text-white/85 truncate">{restaurant.address}</span>
                    <Navigation size={11} className="text-white/70 shrink-0" />
                  </button>
                )}
                {restaurant.phoneNumber && (
                  <a href={`tel:${String(restaurant.phoneNumber).replace(/[\s-]/g, '')}`} className="flex items-center gap-1.5">
                    <Phone size={14} className="text-white/80 shrink-0" />
                    <span className="text-[13px] text-white/85">{restaurant.phoneNumber}</span>
                  </a>
                )}
                <div className="flex items-center gap-1.5">
                  <Clock size={14} className="text-white/80 shrink-0" />
                  <span className="text-[13px] text-white/85">{restaurant.openingHours || 'Hours may vary'}</span>
                </div>
                {likeCount > 0 && (
                  <p className="text-[12px] font-semibold text-white/80 pt-0.5">
                    {likeCount} {likeCount === 1 ? 'person likes' : 'people like'} this restaurant
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-[7px] pt-3 max-w-[1400px] mx-auto">
          {/* iOS HStack — menu content + Menu Sections panel only; the
              Location card, Reviews and footer sit below it at full width */}
          <div className="flex gap-4 items-start">
          {/* Main column */}
          <div className="flex-1 min-w-0">
            {/* iOS search field + "View List" sidebar toggle on small screens */}
            <div className="mt-3 flex items-center gap-1">
              <div className="flex flex-1 items-center gap-2 bg-[#E5E5EA] rounded-[10px] px-3 py-2">
                <Search size={17} className="text-[#8E8E93] shrink-0" />
                <input
                  type="text"
                  placeholder="Search menu items..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="flex-1 min-w-0 bg-transparent text-[15px] text-gray-900 placeholder-[#8E8E93] focus:outline-none"
                />
                {searchText !== '' && (
                  <button onClick={() => setSearchText('')} aria-label="Clear search">
                    <CircleX size={17} className="text-[#8E8E93]" />
                  </button>
                )}
              </div>
              {!isMenuSectionsOpen && menuSections.length > 1 && (
                <button
                  onClick={() => setIsMenuSectionsOpen(true)}
                  className="lg:hidden shrink-0 flex items-center gap-1 bg-red-600 text-white text-[13px] font-semibold px-2.5 py-1.5 rounded-lg"
                >
                  <span>&#x1F4CB;</span>
                  View List
                </button>
              )}
            </div>

            {/* Category chips (mobile / all screens) */}
            {categories.length > 1 && (
              <div className="flex gap-2 overflow-x-auto py-3 scrollbar-hide">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`shrink-0 text-[13px] font-medium px-3.5 py-1.5 rounded-full transition-colors ${!selectedCategory ? 'bg-red-600 text-white' : 'bg-white text-gray-900'}`}
                >
                  All
                </button>
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`shrink-0 text-[13px] font-medium px-3.5 py-1.5 rounded-full transition-colors ${selectedCategory === category ? 'bg-red-600 text-white' : 'bg-white text-gray-900'}`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            )}

            {/* Menu sections */}
            {visibleCategories.length === 0 ? (
              <div className="flex flex-col items-center py-12 gap-2">
                <UtensilsCrossed size={36} className="text-gray-300" />
                <p className="text-[15px] font-semibold text-gray-500">No items found</p>
                <p className="text-[13px] text-[#8E8E93]">Try a different search or category</p>
              </div>
            ) : (
              visibleCategories.map(category => (
                <div
                  key={category}
                  ref={el => { categoryRefs.current[category] = el; }}
                  data-category={category}
                  className="scroll-mt-16 mb-4"
                >
                  {/* iOS category header — red bold name + (count) + "View All".
                      The green ring pulses while a single category is selected,
                      mirroring pulsingGreenGlow(active: selectedCategory != nil). */}
                  <div className="flex items-center gap-1.5 px-1 py-2">
                    <h3 className="text-[17px] font-bold text-[#FF3B30]">{category}</h3>
                    <span className="text-[13px] text-[#8E8E93]">({(groupedMenuItems[category] || []).length})</span>
                    <span className="flex-1" />
                    <button
                      onClick={() => navigate(`/category/${encodeURIComponent(category)}?restaurantId=${restaurantId}&businessType=${businessType}`)}
                      className={`text-xs font-semibold text-[#FF3B30] bg-[#FF3B30]/10 px-3 py-1.5 rounded-lg ${selectedCategory ? 'pulsing-green-glow' : ''}`}
                    >
                      View All
                    </button>
                  </div>
                  {/* iOS ScrollView(.horizontal) — fixed-width cards scroll
                      sideways instead of compressing when the sidebar opens */}
                  <div className="flex gap-3 overflow-x-auto px-1 pb-2">
                    {(groupedMenuItems[category] || []).map(item => (
                      <MenuItemCard
                        key={item.id}
                        item={item}
                        restaurant={restaurant}
                        isFavorite={isFavorite(item.id)}
                        onToggleFavorite={(e) => handleToggleFavorite(item, e)}
                        onClick={() => setSelectedItem(item)}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* iOS "Menu Sections" sidebar — slim inline right panel (iOS 190pt on
              iPad/desktop, 160pt on iPhone). It narrows the content instead of
              covering it; toggled via "View List" on mobile. */}
          {menuSections.length > 1 && (
            <aside
              className={`flex-shrink-0 transition-all duration-300 ${
                isMenuSectionsOpen ? 'w-[150px]' : 'hidden'
              } lg:block ${isSidebarCollapsed ? 'lg:w-[52px]' : 'lg:w-[190px]'}`}
            >
              <div className="sticky top-16 bg-white rounded-xl shadow-sm max-h-[calc(100vh-8rem)] overflow-y-auto">
                {/* iOS header — red "Collapse" pill, then "Menu Sections" title */}
                <div className="p-2 space-y-2 border-b border-black/[0.06]">
                  <button
                    onClick={() => (window.matchMedia('(min-width: 1024px)').matches
                      ? setIsSidebarCollapsed(!isSidebarCollapsed)
                      : setIsMenuSectionsOpen(false))}
                    aria-label={isSidebarCollapsed ? 'Expand menu sections' : 'Collapse menu sections'}
                    className="flex items-center gap-1 bg-red-600 text-white text-[11px] font-semibold px-2 py-1 rounded-md"
                  >
                    {isSidebarCollapsed ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
                    <span className={isSidebarCollapsed ? 'lg:hidden' : ''}>Collapse</span>
                  </button>
                  <h3 className={`text-[14px] font-bold text-gray-900 px-0.5 ${isSidebarCollapsed ? 'lg:hidden' : ''}`}>Menu Sections</h3>
                </div>
                <div className={`p-1.5 ${isSidebarCollapsed ? 'lg:hidden' : ''}`}>
                  <MenuSectionsList
                    sections={menuSections}
                    totalCount={menuItems.length}
                    selectedCategory={selectedCategory}
                    activeCategoryOnScroll={activeCategoryOnScroll}
                    onSelect={selectSection}
                  />
                </div>
              </div>
            </aside>
          )}
          </div>

            {/* Location — iOS mini map card (pin at business coordinates) */}
            {restaurant.coordinates?.latitude ? (
              <div className="bg-white rounded-xl px-4 py-4 mt-2 space-y-4">
                <h3 className="text-[17px] font-semibold text-gray-900">Location</h3>
                {/* Display-only pin map — taps open Google Maps directions
                    like iOS MKMapItem.openMaps; the embed itself is frozen */}
                <button
                  onClick={getDirections}
                  className="block w-full h-[200px] rounded-lg overflow-hidden"
                  aria-label="Get directions"
                >
                  <iframe
                    src={`https://maps.google.com/maps?q=${restaurant.coordinates.latitude},${restaurant.coordinates.longitude}&z=15&output=embed`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    tabIndex={-1}
                    className="pointer-events-none"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`${restaurant.name} map`}
                  />
                </button>

                <div className="border-t border-black/[0.08]" />

                {/* Contact info — iOS mappin.circle.fill / phone.fill / clock.fill */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <MapPin size={20} className="text-[#FF3B30] shrink-0" />
                      <span className="text-[15px] text-gray-900 truncate">
                        {restaurant.parish || restaurant.address}
                      </span>
                    </div>
                    {restaurant.phoneNumber && (
                      <a
                        href={`tel:${String(restaurant.phoneNumber).replace(/[\s-]/g, '')}`}
                        className="flex items-center gap-1.5 shrink-0"
                      >
                        <Phone size={18} className="text-[#34C759]" />
                        <span className="text-[15px] text-gray-900">{restaurant.phoneNumber}</span>
                      </a>
                    )}
                  </div>
                  {restaurant.openingHours && (
                    <div className="flex items-center gap-1.5">
                      <Clock size={18} className="text-[#007AFF]" />
                      <span className="text-[15px] text-gray-900">{restaurant.openingHours}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {/* Reviews */}
            <div className="bg-white rounded-xl overflow-hidden mt-2">
              <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                <h3 className="text-[17px] font-semibold text-gray-900">
                  Reviews {reviews.length > 0 && <span className="text-[#8E8E93] font-normal">({reviews.length})</span>}
                </h3>
                <button
                  onClick={() => { if (!currentUser) { navigate('/login'); return; } setShowReviewModal(true); }}
                  className="text-[13px] font-medium text-[#FF3B30] px-2.5 py-1.5 bg-[#FF3B30]/10 rounded-lg"
                >
                  Write a Review
                </button>
              </div>

              <div className="divide-y divide-black/[0.06]">
                {reviews.length === 0 ? (
                  <p className="px-4 py-8 text-center text-[15px] text-[#8E8E93]">No reviews yet. Be the first to review!</p>
                ) : (
                  reviews.map(review => (
                    <div key={review.id} className="px-4 py-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[15px] font-semibold text-gray-900">
                          {review.userName || review.reviewerName || 'Anonymous'}
                        </span>
                        <div className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map(star => (
                            <Star key={star} size={13} className={star <= (review.rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'} />
                          ))}
                        </div>
                      </div>
                      {(review.comment || review.text) && (
                        <p className="text-[15px] text-gray-700 mb-1">{review.comment || review.text}</p>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-[#8E8E93]">{formatReviewDate(review.createdAt || review.timestamp)}</span>
                        {review.isLiked && (
                          <span className="flex items-center gap-1 text-xs text-[#8E8E93]">
                            <ThumbsUp size={12} /> Liked
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <Footer />
          </div>

        {/* Floating cart FAB */}
        {cartItems.length > 0 && (
          <button
            onClick={() => setShowCartSheet(true)}
            className="fixed bottom-[100px] right-4 z-50 w-14 h-14 rounded-full bg-[#FF3B30] shadow-lg flex items-center justify-center active:scale-95 transition-transform"
            aria-label="Open cart"
          >
            <ShoppingCart size={24} className="text-white" />
            {itemCount > 0 && (
              <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#34C759] text-white text-xs font-bold flex items-center justify-center">
                {itemCount > 99 ? '99+' : itemCount}
              </span>
            )}
          </button>
        )}

        {/* Cart bottom sheet */}
        <CartSheet open={showCartSheet} onClose={() => setShowCartSheet(false)} />

        {/* Toast */}
        {showToast && (
          <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 bg-gray-900/90 text-white text-[15px] px-4 py-2.5 rounded-full flex items-center gap-2 shadow-lg">
            <CheckCircle size={16} className="text-[#30D158]" />
            {toastMessage}
          </div>
        )}

        {/* Menu item detail modal */}
        {selectedItem && (
          <MenuItemDetailModal
            item={selectedItem}
            restaurant={restaurant}
            isOffline={isAppOffline}
            onClose={() => setSelectedItem(null)}
          />
        )}

        {/* Review modal — iOS ReviewView style sheet */}
        {showReviewModal && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowReviewModal(false)}>
            <div className="absolute inset-0 bg-black/60" />
            <div className="relative bg-[#F2F2F7] rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <h3 className="text-[17px] font-semibold text-gray-900">
                  {userReview ? 'Update Your Review' : 'Write a Review'}
                </h3>
                <button onClick={() => setShowReviewModal(false)} className="w-7 h-7 rounded-full bg-[#E5E5EA] flex items-center justify-center" aria-label="Close">
                  <X size={14} className="text-[#6D6D72]" />
                </button>
              </div>

              {reviewSuccess ? (
                <div className="flex flex-col items-center py-6 gap-2">
                  <CheckCircle size={40} className="text-[#30D158]" />
                  <p className="text-[15px] font-semibold text-gray-900">Thank you for your review!</p>
                  <p className="text-[13px] text-[#8E8E93]">It will appear once approved.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-center gap-2">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewRating(star)}
                        onMouseEnter={() => setReviewHover(star)}
                        onMouseLeave={() => setReviewHover(0)}
                        aria-label={`${star} star${star > 1 ? 's' : ''}`}
                      >
                        <Star
                          size={32}
                          className={(reviewHover || reviewRating) >= star ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                        />
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    placeholder="Share your experience..."
                    rows={4}
                    className="w-full bg-white rounded-xl px-3 py-2.5 text-[15px] text-gray-900 placeholder-[#8E8E93] focus:outline-none resize-none ring-1 ring-black/5"
                  />
                  <button
                    onClick={submitReview}
                    disabled={reviewSubmitting || !reviewText.trim()}
                    className="w-full flex items-center justify-center gap-2 text-white text-[17px] font-semibold bg-red-600 py-3 rounded-xl disabled:opacity-50"
                  >
                    <Send size={16} />
                    {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Owner photo modal */}
        {/* Owner photo — iOS FullSizeImageView: black sheet, image
            scaledToFit full-screen, nav title + Done button */}
        {showOwnerPhotoModal && ownerProfileImageURL && (
          <div className="fixed inset-0 z-50 bg-black flex flex-col" onClick={() => setShowOwnerPhotoModal(false)}>
            <div className="flex items-center px-4 py-3">
              <div className="w-12" />
              <h3 className="text-[17px] font-semibold text-white flex-1 text-center">Owner Profile Photo</h3>
              <button onClick={() => setShowOwnerPhotoModal(false)} className="w-12 text-right text-[17px] text-white font-medium">
                Done
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center min-h-0 px-2 pb-6">
              <img
                src={ownerProfileImageURL}
                alt={ownerName || 'Owner'}
                className="max-w-full max-h-full object-contain"
                onClick={e => e.stopPropagation()}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// Menu item card — mirrors iOS MenuItemCard: fixed 160x100 card where the
// image IS the background, dark gradient, name + price + favorite heart
// overlaid at the bottom. No quick-add — tapping opens the detail view.
const MenuItemCard = React.memo(function MenuItemCard({ item, restaurant, isFavorite, onToggleFavorite, onClick }) {
  const imageURL = item.menuItemImageURL || item.imageURL || restaurant?.bannerURL || '';
  const outOfStock = item.trackStock === true && (item.stockCount ?? 0) <= 0;

  // iOS truncatedName — first two words when the name is long
  const words = (item.name || '').split(' ');
  const displayName =
    (item.name || '').length > 7 && words.length > 2 ? words.slice(0, 2).join(' ') : item.name;

  return (
    <div
      onClick={onClick}
      className="relative w-40 h-[100px] shrink-0 rounded-xl overflow-hidden bg-gray-300 shadow-sm ring-1 ring-black/10 cursor-pointer active:opacity-80 transition-opacity"
    >
      {/* Image — fit inside the card frame (no crop), blurred cover of the
          same image fills the letterbox so portrait uploads stay clean */}
      {imageURL ? (
        <>
          <img
            src={imageURL}
            alt=""
            aria-hidden
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover blur-md scale-125 opacity-80"
          />
          <img src={imageURL} alt={item.name} loading="lazy" className="absolute inset-0 w-full h-full object-contain" />
        </>
      ) : (
        <div className="absolute inset-0 bg-gray-300 flex items-center justify-center">
          <UtensilsCrossed size={28} className="text-gray-400" />
        </div>
      )}

      {/* Dark gradient backdrop so text is always legible (iOS LinearGradient) */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/80" />

      {/* Rating badge top-right — same as the home menu cards */}
      {restaurant?.rating > 0 && (
        <div className="absolute top-2 right-2 flex items-center gap-0.5 px-1.5 py-[3px] bg-black/60 rounded-lg z-10">
          <Star size={11} className="text-yellow-400 fill-yellow-400" />
          <span className="text-[11px] font-semibold text-white">{Number(restaurant.rating).toFixed(1)}</span>
        </div>
      )}

      {/* Overlay content: name, price + heart */}
      <div className="absolute inset-x-0 bottom-0 p-3">
        <p className="text-[13px] font-semibold text-white truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
          {displayName}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold text-[#30D158] drop-shadow-[0_1px_0_rgba(0,0,0,0.8)]">
            ${(item.price || 0).toFixed(2)}
          </span>
          <button onClick={onToggleFavorite} className="-m-1 p-1" aria-label="Favorite">
            <Heart
              size={15}
              className={isFavorite ? 'text-[#FF3B30] drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]' : 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]'}
              fill={isFavorite ? '#FF3B30' : 'none'}
            />
          </button>
        </div>
      </div>

      {/* Out of Stock overlay — iOS black 0.45 + badge */}
      {outOfStock && (
        <div className="absolute inset-0 bg-black/45 flex items-center justify-center">
          <span className="px-2 py-1 rounded-lg bg-black/70 text-white text-[11px] font-bold uppercase">
            Out of Stock
          </span>
        </div>
      )}
    </div>
  );
});

// iOS "Menu Sections" list — "All" row plus one row per category with its
// item count; the selected section is highlighted red.
function MenuSectionsList({ sections, totalCount, selectedCategory, activeCategoryOnScroll, onSelect }) {
  const rowClass = (isActive) =>
    `w-full text-left px-2.5 py-2 rounded-lg text-[13px] transition-colors flex items-center justify-between gap-1 ${
      isActive ? 'bg-red-600 text-white font-medium' : 'text-gray-700 active:bg-gray-100'
    }`;
  const countClass = (isActive) => `text-[11px] ml-1 flex-shrink-0 ${isActive ? 'text-white/80' : 'text-[#8E8E93]'}`;

  return (
    <nav className="space-y-1">
      <button onClick={() => onSelect(null)} className={rowClass(selectedCategory === null)}>
        <span>All</span>
        <span className={countClass(selectedCategory === null)}>{totalCount}</span>
      </button>
      {sections.map(({ name, count }) => {
        const isActive = selectedCategory === name || activeCategoryOnScroll === name;
        return (
          <button key={name} onClick={() => onSelect(name)} className={rowClass(isActive)}>
            <span className="min-w-0 break-words">
              {name.length > 15
                ? name.split(' ').map((word, i) => <span key={i} className="block">{word}</span>)
                : name}
            </span>
            <span className={countClass(isActive)}>{count}</span>
          </button>
        );
      })}
    </nav>
  );
}

export default RestaurantDetailPage;
